import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/auth/auth-layout";
import { Button } from "@/components/ui/button";
import { Field, FormError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/lib/api";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";
import { NAME_MAX, SLUG_MAX, checkSlug, cleanName, slugify } from "@/lib/slugs";

export const Route = createFileRoute("/_app/new")({
  component: NewWorkspacePage,
});

function NewWorkspacePage() {
  const { t } = useTranslation("shell");
  const { t: tErrors } = useTranslation("errors");
  const navigate = useNavigate();
  const me = useQuery(api.users.me);
  const workspaces = useQuery(api.workspaces.listMine);
  const [name, setName] = useState("");
  // The address follows the name until the user edits it.
  const [slugOverride, setSlugOverride] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);

  const slug = slugOverride ?? slugify(name);

  // The workspace is mirrored into the app in the same transaction that
  // creates it; open it once this client has seen it, not a moment before.
  useEffect(() => {
    if (createdSlug && workspaces?.some((workspace) => workspace.slug === createdSlug)) {
      void navigate({ to: "/$workspaceSlug", params: { workspaceSlug: createdSlug } });
    }
  }, [createdSlug, workspaces, navigate]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const cleaned = cleanName(name);
    const slugProblem = checkSlug(slug);
    setNameError(cleaned ? null : tErrors("nameInvalid"));
    setSlugError(slugProblem ? tErrors(slugProblem) : null);
    setError(null);
    if (!cleaned || slugProblem) return;

    setPending(true);
    const result = await authClient.organization.create({ name: cleaned, slug });
    if (result.error) {
      const message = authErrorMessage(result.error, tErrors);
      if (result.error.code?.includes("SLUG")) setSlugError(message);
      else setError(message);
      setPending(false);
      return;
    }
    setCreatedSlug(slug);
  }

  return (
    <AuthLayout
      title={t("createWorkspace.title")}
      description={t("createWorkspace.description")}
      footer={
        me ? (
          <>
            {t("createWorkspace.signedInAs", { email: me.email })}{" "}
            <button
              type="button"
              onClick={() => void authClient.signOut()}
              className="text-foreground underline decoration-border-strong underline-offset-4 hover:decoration-foreground"
            >
              {t("user.signOut")}
            </button>
          </>
        ) : null
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label={t("createWorkspace.name")} htmlFor="workspace-name" error={nameError}>
          <Input
            id="workspace-name"
            autoFocus
            autoComplete="organization"
            maxLength={NAME_MAX}
            value={name}
            aria-invalid={Boolean(nameError)}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field
          label={t("createWorkspace.slug")}
          htmlFor="workspace-slug"
          error={slugError}
          hint={`${window.location.host}/${slug || "…"}`}
        >
          <Input
            id="workspace-slug"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={SLUG_MAX}
            value={slug}
            aria-invalid={Boolean(slugError)}
            onChange={(event) => setSlugOverride(event.target.value.toLowerCase())}
          />
        </Field>
        <FormError>{error}</FormError>
        <Button type="submit" disabled={pending}>
          {pending ? <Spinner /> : null}
          {t("createWorkspace.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
